import { useCallback, useEffect, useRef, useState } from 'react';
import { streamCraJob } from '../api/client.js';

/**
 * Hook qui s'abonne au stream SSE d'un job CRA et expose :
 *  - events : tableau d'événements reçus en ordre chronologique
 *  - status : 'idle' | 'streaming' | 'completed' | 'failed'
 *  - progress : { done, total } reflet du dernier consultant_start
 *  - close() : ferme manuellement la connexion
 *
 * Passe `jobId = null` pour ne rien démarrer.
 */
export function useGenerationStream(jobId) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (!jobId) {
      setEvents([]);
      setStatus('idle');
      setProgress({ done: 0, total: 0 });
      return undefined;
    }

    setEvents([]);
    setStatus('streaming');
    setProgress({ done: 0, total: 0 });

    const cleanup = streamCraJob(jobId, {
      onEvent: (event) => {
        setEvents((prev) => [...prev, event]);
        if (event.consultantsTotal != null) {
          setProgress((prev) => ({
            done: event.consultantsDone ?? prev.done,
            total: event.consultantsTotal,
          }));
        } else if (event.consultantsDone != null) {
          setProgress((prev) => ({ ...prev, done: event.consultantsDone }));
        }
        if (event.type === 'job_completed') setStatus('completed');
        if (event.type === 'job_failed') setStatus('failed');
      },
      onError: () => setStatus((s) => (s === 'streaming' ? 'failed' : s)),
      onClose: () => setStatus((s) => (s === 'streaming' ? 'completed' : s)),
    });

    cleanupRef.current = cleanup;
    return () => {
      cleanup();
      cleanupRef.current = null;
    };
  }, [jobId]);

  const close = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  return { events, status, progress, close };
}
